import type { NotificationCategory } from '@package/drizzle/schema'
import { findFamilyConnectionsForChildren, isSchoolOrganization } from '@package/drizzle/queries'

import type { GuardianNotificationType } from './entity'
import type { DatabaseOrTransaction, NotificationContext, NotificationSnapshot } from './types'
import { emitRollupDelta } from './rollup'

/**
 * The parent side of every notification a connected child receives.
 *
 * Once a child is home-school connected (Sambung Sekolah-Rumah: a member of a school **and** a
 * family organization), the parent has to see the school news their child gets. Only school news:
 * the mirror runs when the event came from a school organization and the child is in a school.
 * Anything sent inside the family — a parent assigning a course or recommending a book to their
 * own child — was done by the parent, who already holds the actor's card for it. They are not
 * added as an extra recipient on the child's own event: that copy is written in the second person
 * ("tugas baru buat kamu"), and `(eventId, userId, audience)` is unique, so a parent with two
 * children in one class would get one card for two children. Instead each producer calls this
 * helper beside its own emit, and it writes a separate `guardian.*` event per (parent, child) with
 * third-person copy that names the child.
 *
 * Read `docs/users-and-organizations.md` for what "connected" means — the family `member` row, not
 * a request status.
 */

/** Snapshot fields this helper fills in per child; callers must not set them. */
type GuardianSnapshot = Omit<
  NotificationSnapshot,
  'studentId' | 'studentName' | 'studentAvatarUrl' | 'actorIsRecipient' | 'organizationId' | 'organizationName'
>

export interface EmitGuardianMirrorInput {
  type: GuardianNotificationType
  /**
   * `ASSIGNMENT` or `SHARE`, matching the event being mirrored. Deliberately not a category of
   * its own: `notification_category_enum` holds only `SHARE | ASSIGNMENT | SYSTEM` in every
   * migrated environment, and a parent looking for their child's school news looks under the
   * same tab they already use.
   */
  category: NotificationCategory
  /**
   * The organization the mirrored event happened in: the course's, the enrolment's, or the
   * sender's active organization. Required, so a new producer has to say where its news comes
   * from. Anything other than a school organization — a family, or `null` — mirrors nothing.
   *
   * A producer whose children reached the event through different organizations (a challenge
   * whitelisted to several) calls this once per organization.
   */
  sourceOrganizationId: string | null
  /**
   * Whoever acted, or null for a cron.
   *
   * A parent who acts on their own child in a school — a teacher who is also the parent — is
   * **not** skipped: they hold the `ACTOR` row for the send ("Budi menerima rekomendasi buku X
   * darimu"), and they still get the guardian card, because the two cards answer different
   * questions. The helper records the overlap as `snapshot.actorIsRecipient`, which is what lets
   * the copy say "Kamu ..." rather than naming the reader in the third person.
   */
  actorId: string | null
  /**
   * The children whose parents should be told. Ones with no family connection, or with a family
   * but no school, are skipped.
   */
  childIds: string[]
  /**
   * The tail of the dedupe key, after the guardian type, the parent and the child. Pass the same
   * entity/time bucket the child-side producer uses — a course id, or a course id plus the Jakarta
   * day key — so the parent's card groups exactly like the child's.
   */
  bucket: string
  entity?: { type: string; id: string }
  snapshot?: GuardianSnapshot
  context?: NotificationContext
  /**
   * Occurrences this call adds, as on {@link emitRollupDelta}. Use `0` wherever the child side
   * uses plain `emit` with a dedupe key: those producers are re-run on a schedule, and a default
   * delta would tick the parent's count once an hour for news that has not changed.
   */
  subjectDelta?: number
  resurface?: boolean
}

export interface EmitGuardianMirrorResult {
  /** Parents a card was written or updated for. */
  mirrored: number
  /**
   * Children skipped: the event did not come from a school, no family connection, not in a
   * school, no parent, or the "parent" is the child themselves.
   */
  skipped: number
}

/**
 * Writes one guardian card per connected child.
 *
 * **Never throws.** Every child-side producer is best-effort — `safelyNotify` in
 * `packages/api/src/utils/assignment-notification.ts`, `perCourse` in the digest cron — and a
 * parent's courtesy card is even further from the real work than a child's. Failures are logged
 * and the caller continues; one parent failing does not stop the rest.
 *
 * `db` is a `DatabaseOrTransaction` so this works inside a router transaction and with `dbPool`
 * in a Trigger.dev task, where the neon-http `db` cannot open the interactive transaction
 * `emitRollupDelta` needs.
 */
export async function emitGuardianMirror(
  db: DatabaseOrTransaction,
  input: EmitGuardianMirrorInput,
): Promise<EmitGuardianMirrorResult> {
  const childIds = [...new Set(input.childIds)]
  if (childIds.length === 0) return { mirrored: 0, skipped: 0 }

  let connections
  try {
    // Family news is not mirrored back to the family. Checked first: it is one row, and it
    // rules out every child at once.
    if (!input.sourceOrganizationId || !(await isSchoolOrganization(db, input.sourceOrganizationId))) {
      return { mirrored: 0, skipped: childIds.length }
    }

    connections = await findFamilyConnectionsForChildren(db, childIds)
  } catch (error) {
    console.error(`[guardian-notification] ${input.type} lookup failed`, error)
    return { mirrored: 0, skipped: childIds.length }
  }

  let mirrored = 0
  let skipped = 0

  for (const childId of childIds) {
    const connection = connections.get(childId)
    const parentUserId = connection?.parentUserId

    // Not connected, a family-only child with no school, a family organization with no owner
    // left, or the "parent" resolved to the child themselves — in every case there is nobody to
    // tell. A parent who is also the actor is deliberately *not* skipped; see `actorId` above.
    if (!connection?.inSchool || !parentUserId || parentUserId === childId) {
      skipped += 1
      continue
    }

    // The reader and the actor are the same person, so the copy addresses them directly. Written
    // only when true: the renderer reads an absent field and `false` the same way, and this
    // snapshot is frozen on the first occurrence of the roll-up.
    const actorIsRecipient = parentUserId === input.actorId

    try {
      await emitRollupDelta(db, {
        type: input.type,
        category: input.category,
        actorId: input.actorId,
        recipientIds: [parentUserId],
        entity: input.entity,
        snapshot: {
          ...input.snapshot,
          studentId: childId,
          studentName: connection.childName,
          studentAvatarUrl: connection.childImage ?? undefined,
          // The family organization the card resolves in. A parent reads one feed from whichever
          // app they are in, and `getChildDetails` is scoped to the active organization: a teacher
          // in the school app clicking a card about a home-only child lands on nothing unless the
          // click first switches to this organization. Frozen with the rest of the snapshot; a
          // child moved out of the family later fails the switch with a message, not silently.
          organizationId: connection.familyOrganizationId,
          organizationName: connection.familyOrganizationName,
          ...(actorIsRecipient ? { actorIsRecipient: true } : {}),
        },
        context: input.context,
        // The parent, the child and the child-side bucket. The parent is in the key because two
        // parents watching the same child must each get their own card; the child is in it
        // because a parent with two children in one class must get two.
        dedupeKey: `${input.type}:${parentUserId}:${childId}:${input.bucket}`,
        subjectDelta: input.subjectDelta,
        resurface: input.resurface,
      })
      mirrored += 1
    } catch (error) {
      console.error(`[guardian-notification] ${input.type} failed childId=${childId}`, error)
      skipped += 1
    }
  }

  return { mirrored, skipped }
}
